/**
 * A session's conversation as the relay can show it: what was sent from here, and the agent's
 * activity from the event ring. When the workbench exposes `sessions.transcript`, the turns
 * themselves are shown instead.
 */

import { time } from '@aioe/shared/wording/dateTime';
import { StatusMark } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import type { RingEvent, Workbench } from '../useWorkbench.ts';
import { Markdown } from '../../components/Markdown.tsx';

export interface Sent {
  at: number;
  text: string;
  outcome?: string;
}
interface Turn {
  role: 'user' | 'assistant' | string;
  text: string;
  at?: string;
}

function describe(e: RingEvent): { text: string; state: 'working' | 'low' | 'error' | 'idle' | 'medium' } {
  const p = e.payload as Record<string, unknown>;
  switch (e.kind) {
    case 'agent.tool_call':
      return { text: `Using ${String(p.title ?? p.kind ?? 'a tool')}`, state: 'working' };
    case 'agent.tool_request':
      return { text: `Wants approval: ${String(p.title ?? 'a tool call')}`, state: 'medium' };
    case 'agent.turn_complete':
      return { text: `Finished the turn (${String(p.stopReason ?? 'done')})`, state: 'low' };
    case 'agent.prompt_submitted':
      return { text: 'Prompt received', state: 'idle' };
    case 'agent.error':
      return { text: String(p.message ?? 'error'), state: 'error' };
    case 'agent.status':
    case 'agent.status_change':
      return { text: `Now ${String(p.status ?? '').replace('_', ' ')}`, state: 'idle' };
    default:
      return { text: e.kind, state: 'idle' };
  }
}

export function Conversation({ w, sessionId, sent }: { w: Workbench; sessionId?: string; sent: Sent[] }) {
  const transcript = useLoad<Turn[] | undefined>(
    async () => (sessionId && w.connected ? ((await w.tryCall<{ turns: Turn[] }>('sessions.transcript', { sessionId, limit: 60 }))?.turns ?? undefined) : undefined),
    [sessionId, w.connected],
    5_000,
  );
  if (transcript.data?.length) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 px-6 py-6">
        {transcript.data.map((t, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${t.role === 'user' ? 'self-end whitespace-pre-wrap bg-accent/15' : 'glass-card self-start'}`}>
            {t.role === 'user' ? t.text : <Markdown breaks text={t.text} />}
          </div>
        ))}
      </div>
    );
  }
  const activity = w.events.filter((e) => (e.payload as { sessionId?: string }).sessionId === sessionId);
  const items = [
    ...sent.map((s) => ({ at: s.at, kind: 'sent' as const, s })),
    ...activity.map((e) => ({ at: e.at, kind: 'event' as const, e })),
  ].sort((a, b) => a.at - b.at);
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-2 px-6 py-6">
      {items.map((it, i) =>
        it.kind === 'sent' ? (
          <div key={`s${i}`} className="max-w-[85%] self-end whitespace-pre-wrap rounded-2xl bg-accent/15 px-4 py-2.5 text-sm">
            {it.s.text}
            {it.s.outcome && <div className="mt-1 text-2xs text-muted">{it.s.outcome === 'routed' ? 'Added to its Chat conversation' : it.s.outcome === 'queued' ? 'Queued behind the current turn' : 'Sent'}</div>}
          </div>
        ) : (
          <div key={`e${it.e.seq}`} className="flex items-center gap-2 self-start text-xs text-muted">
            <span className="mono">{time(it.at, { seconds: true })}</span>
            <StatusMark state={describe(it.e).state} label={describe(it.e).text} />
          </div>
        ),
      )}
      <p className="mt-2 self-center text-2xs text-muted">The agent's replies appear in the app; this page shows its activity until the workbench shares transcripts.</p>
    </div>
  );
}
